// ── ManualPage — 매장 안내서 (V82~V83) ──────────────────────
// 연로하신 부모님이 보고 운영할 수 있도록 큰 글씨 + 단순 레이아웃.
// 3개 서브뷰: 메뉴 레시피 / 단가표 / 발주서 양식.
// 종이 인쇄(A4) 친화 — @media print 룰로 메뉴/버튼 숨김.
//
// V83 변경:
//   - 메뉴 레시피 화면에서 직접 편집 가능
//   - 재료 표에 [재료 | 용량 | 손질법] 3컬럼
//   - 재료 드래그앤드롭 또는 ▲▼ 버튼으로 순서 변경
//
// 설계 원칙:
//   - 본문 18pt, 제목 28pt 이상
//   - 색 의존 X (검정·진회색만)
//   - 약어/외래어 풀어쓰기
//   - 옵션 최소 (큰 버튼)

import React, { useState, useMemo, useRef } from 'react';
import OperationsManual from './OperationsManual';
import { useLang } from '../i18n/LangContext';        // V147 중국어 화면
import { buildZhMap, zhOf } from '../i18n/BiName';
import { specToZh, hasHangul } from '../i18n/units';

// V147: 중국어 화면 — 글자는 중국어로, 메뉴·재료 이름은 중국어 이름(없으면 원래 이름)으로 보이고 인쇄된다
function useZ() {
  const { lang, fmt } = useLang();
  const zh = lang === 'zh';
  return { zh, Z: (ko, z) => (zh ? z : ko), money: fmt };
}
const todayZh = () => {
  const d = new Date();
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
};
const CAT_ZH = { '신선식품': '生鲜', '공산품': '干货 · 调料', '소모품': '消耗品', '반제품': '半成品', '미분류': '未分类' };

const PRINT_STYLES = `
  @media print {
    /* 1) 페이지 여백·기본 reset */
    @page { margin: 14mm 12mm; }
    html, body {
      height: auto !important;
      overflow: visible !important;
      background: #fff !important;
      margin: 0 !important; padding: 0 !important;
    }
    /* 2) 모든 컨테이너 overflow 풀어주기 (App 메인 컨테이너 height 제약 무시) */
    body, body * {
      overflow: visible !important;
    }
    /* 3) React 앱 chrome 전부 숨기고 매뉴얼 본문만 보이게 */
    body * { visibility: hidden !important; }
    .manual-page, .manual-page * { visibility: visible !important; }
    .manual-page {
      position: absolute !important;
      left: 0 !important; top: 0 !important;
      width: 100% !important;
      padding: 0 !important; margin: 0 !important;
      box-shadow: none !important;
      max-width: none !important;
    }
    /* 4) no-print 영역 — 화면용 버튼/상단바는 인쇄에서 완전히 삭제 */
    .no-print { display: none !important; }
    /* 5) 표·블록 페이지 나눔 가이드 */
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; page-break-after: auto; }
    h2 { page-break-before: auto; }
    .recipe-block, .vendor-block { page-break-inside: avoid; }
    /* 6) 숨김 처리된 메뉴는 인쇄에서 완전 제외 */
    .hide-on-print { display: none !important; }
    /* 7) 운영 매뉴얼 — 오픈/영업/마감 각 섹션 페이지 분리 */
    .sop-section { page-break-inside: avoid; page-break-after: always; }
    .sop-section:last-of-type { page-break-after: auto; }

    /* 8) V88 — 부모님 시력용 대형 글자. inline fontSize를 !important로 override */
    .manual-page,
    .manual-page * {
      font-size: 28pt !important;
      line-height: 1.45 !important;
    }
    /* 큰 헤더 — 페이지 첫 머리(매장 안내서/메뉴 만드는 법 등) */
    .manual-page h1,
    .manual-page > div:first-child > div:first-child {
      font-size: 42pt !important;
    }
    /* h2 (카테고리 헤더, 섹션 헤더) */
    .manual-page h2 {
      font-size: 36pt !important;
    }
    /* 메뉴 이름 (recipe-block 첫 줄) */
    .manual-page .recipe-block > div:first-child > div:first-child > div:first-child {
      font-size: 38pt !important;
    }
    /* 표 헤더·셀 */
    .manual-page table th,
    .manual-page table td {
      font-size: 26pt !important;
      padding: 14px 14px !important;
    }
    /* 인쇄 머리글 (PrintHeader 안 소제목) */
    .manual-page > div:first-child > div:nth-child(2) {
      font-size: 20pt !important;
    }
    /* SOP 섹션 번호+라벨 */
    .manual-page .sop-section > div:first-child > div:first-child > div:first-child {
      font-size: 38pt !important;
    }
    /* V89 — SOP 섹션 강화 (오픈/영업/마감 표를 더 크게) */
    /* SOP 섹션 부제 (영업 시작 전 준비 등) */
    .manual-page .sop-section > div:first-child > div:first-child > div:nth-child(2) {
      font-size: 22pt !important;
    }
    /* SOP 표 셀 — 시간/할 일/메모 (기본 본문 28pt → 32pt) */
    .manual-page .sop-section table th,
    .manual-page .sop-section table td {
      font-size: 32pt !important;
      padding: 18px 16px !important;
      line-height: 1.4 !important;
    }
    /* 체크박스 □ — 첫 컬럼 (직원이 눈에 잘 보이게 매우 크게) */
    .manual-page .sop-section table td:first-child {
      font-size: 50pt !important;
      font-weight: 700 !important;
      line-height: 1 !important;
    }
  }
`;

const fmtWon = (n) => Math.round(Number(n) || 0).toLocaleString('ko-KR');
const todayKo = () => {
  const d = new Date();
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

// ── 진입 화면: 3개 큰 카드 ─────────────────────────────────
function MenuChoice({ onSelect, storeName }) {
  const { zh, Z } = useZ();
  const cards = [
    { key: 'sop',     title: Z('오픈·영업·마감 매뉴얼', '开店 · 营业 · 关店流程'), desc: Z('하루 운영 순서를 표로 정리했어요. 직원이 보고 따라할 수 있게 인쇄하세요.', '把一天的工作顺序整理成表格，打印出来让员工照着做。') },
    { key: 'recipes', title: Z('메뉴 만드는 법', '菜品做法'),         desc: Z('메뉴별 재료·용량·손질법을 적어두고 인쇄해서 주방에 붙여 두세요.', '每道菜的食材 · 用量 · 处理方法，打印出来贴在厨房。') },
    { key: 'prices',  title: Z('재료 가격표', '食材价格表'),           desc: Z('거래처별로 재료의 가격을 정리한 표예요. 가격 비교에 좋아요.', '按供应商整理的食材价格，方便比价。') },
    { key: 'order',   title: Z('발주서 양식', '订货单样式'),           desc: Z('거래처에 보낼 주문서 양식이에요. 필요할 때마다 인쇄하세요.', '发给供应商的订货单空白表，需要时打印。') },
  ];

  return (
    <div className="no-print" style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#18181B', marginBottom: 8, letterSpacing: '-0.02em' }}>
        {Z('매장 안내서', '门店手册')}
      </div>
      <div style={{ fontSize: 18, color: '#52525B', lineHeight: 1.6, marginBottom: 28 }}>
        {zh ? <>{storeName ? `${storeName}的` : ''}菜品 · 食材 · 订货单都可以打印出来放在店里。请选择要看的内容。</>
            : <>{storeName ? `${storeName}의 ` : ''}메뉴·재료·발주서를 종이로 인쇄해서 매장에 두실 수 있어요.
        보고 싶은 것을 골라 주세요.</>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {cards.map(c => (
          <button key={c.key} onClick={() => onSelect(c.key)}
            style={{
              padding: '24px 24px', background: '#fff',
              border: '2px solid #18181B', borderRadius: 12,
              textAlign: 'left', cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
          >
            <div style={{ fontSize: 22, fontWeight: 800, color: '#18181B', marginBottom: 6 }}>{c.title}</div>
            <div style={{ fontSize: 16, color: '#52525B', lineHeight: 1.5 }}>{c.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── 공통: 상단 바 ──────────────────────────────────────────
function TopBar({ title, onBack, hidePrint }) {
  const { Z } = useZ();
  return (
    <div className="no-print" style={{
      position: 'sticky', top: 56, zIndex: 90, background: '#fff',
      borderBottom: '2px solid #18181B', padding: '14px 20px',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <button onClick={onBack}
        style={{
          padding: '12px 20px', fontSize: 18, fontWeight: 700,
          background: '#fff', color: '#18181B',
          border: '2px solid #18181B', borderRadius: 8, cursor: 'pointer',
        }}>
        {Z('← 돌아가기', '← 返回')}
      </button>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#18181B', flex: 1, minWidth: 120 }}>
        {title}
      </div>
      {!hidePrint && (
        <button onClick={() => window.print()}
          style={{
            padding: '14px 28px', fontSize: 20, fontWeight: 800,
            background: '#18181B', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}>
          {Z('종이로 인쇄', '打印')}
        </button>
      )}
    </div>
  );
}

function PrintHeader({ title, storeName }) {
  const { zh } = useZ();
  return (
    <div style={{ borderBottom: '3px solid #18181B', paddingBottom: 14, marginBottom: 22 }}>
      <div style={{ fontSize: 32, fontWeight: 800, color: '#18181B', marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ fontSize: 16, color: '#52525B' }}>
        {storeName && <>{storeName} &middot; </>}
        {zh ? `打印日期：${todayZh()}` : `인쇄일: ${todayKo()}`}
      </div>
    </div>
  );
}

// ── 큰 input 스타일 (부모님 친화) ──────────────────────────
const bigInput = {
  width: '100%', padding: '10px 12px',
  fontSize: 18, fontWeight: 600,
  color: '#18181B', background: '#fff',
  border: '2px solid #A1A1AA', borderRadius: 6,
  outline: 'none', boxSizing: 'border-box',
};

// ── 1. 메뉴 레시피 (편집 가능) ─────────────────────────────
function RecipesView({ categories, prices, onCategoriesChange, onBack, storeName }) {
  const { zh, Z } = useZ();
  const zhMap = useMemo(() => buildZhMap(prices), [prices]);
  // 편집 중 식별자: `${categoryIdx}-${itemIdx}` 또는 null
  const [editingKey, setEditingKey] = useState(null);
  const [editItem, setEditItem]     = useState(null);
  const dragIdxRef = useRef(null);

  const recipeGroups = useMemo(() => {
    return (categories || []).map(cat => ({
      name: cat.name,
      name_zh: cat.name_zh,
      items: cat.items || [],
    })).filter(g => g.items.length > 0);
  }, [categories]);

  // 단가표에서 재료명 unique 추출 — 입력 자동완성용
  const ingredientOptions = useMemo(() => {
    const set = new Set();
    (prices || []).forEach(p => {
      if (p && p.name && p.name.trim()) set.add(p.name.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [prices]);

  const startEdit = (catIdx, itemIdx, item) => {
    setEditingKey(`${catIdx}-${itemIdx}`);
    setEditItem({
      ...item,
      ingredients: (item.ingredients || []).map(i => ({
        name: i.name || '',
        qty: i.qty != null ? String(i.qty) : '',
        prep: i.prep || '',
      })),
    });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditItem(null);
  };

  const saveEdit = (catIdx, itemIdx) => {
    if (!editItem) return;
    // 빈 재료 행 정리
    const cleaned = {
      ...editItem,
      ingredients: editItem.ingredients
        .filter(i => i.name && i.name.trim())
        .map(i => ({
          name: i.name.trim(),
          qty: Number(i.qty) || 0,
          ...(i.prep && i.prep.trim() ? { prep: i.prep.trim() } : {}),
        })),
    };
    const next = (categories || []).map((cat, ci) => {
      if (ci !== catIdx) return cat;
      return {
        ...cat,
        items: (cat.items || []).map((it, ii) =>
          ii === itemIdx ? cleaned : it
        ),
      };
    });
    if (typeof onCategoriesChange === 'function') onCategoriesChange(next);
    cancelEdit();
  };

  const updateField = (field, value) => {
    setEditItem(prev => ({ ...prev, [field]: value }));
  };

  // V87: 메뉴 카드 숨김 토글 (편집 모드 외에서도 한 클릭으로 즉시 저장)
  const toggleHidden = (catIdx, itemIdx) => {
    const next = (categories || []).map((cat, ci) => {
      if (ci !== catIdx) return cat;
      return {
        ...cat,
        items: (cat.items || []).map((it, ii) =>
          ii === itemIdx ? { ...it, hidden: !it.hidden } : it
        ),
      };
    });
    if (typeof onCategoriesChange === 'function') onCategoriesChange(next);
  };

  const updateIng = (idx, field, value) => {
    setEditItem(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) =>
        i === idx ? { ...ing, [field]: value } : ing
      ),
    }));
  };

  const addIng = () => {
    setEditItem(prev => ({
      ...prev,
      ingredients: [...(prev.ingredients || []), { name: '', qty: '', prep: '' }],
    }));
  };

  const removeIng = (idx) => {
    setEditItem(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== idx),
    }));
  };

  const moveIng = (from, to) => {
    if (!editItem) return;
    if (to < 0 || to >= editItem.ingredients.length) return;
    const next = [...editItem.ingredients];
    const [removed] = next.splice(from, 1);
    next.splice(to, 0, removed);
    setEditItem(prev => ({ ...prev, ingredients: next }));
  };

  // 드래그앤드롭
  const onDragStart = (idx) => { dragIdxRef.current = idx; };
  const onDragOver  = (e)   => e.preventDefault();
  const onDrop = (idx) => {
    const from = dragIdxRef.current;
    if (from == null || from === idx) return;
    moveIng(from, idx);
    dragIdxRef.current = null;
  };

  return (
    <div>
      <TopBar title={Z('메뉴 만드는 법', '菜品做法')} onBack={onBack} hidePrint={!!editingKey} />
      <div className="manual-page" style={{
        maxWidth: 880, margin: '0 auto', padding: '24px 20px',
        fontSize: 18, color: '#18181B', lineHeight: 1.6,
      }}>
        <PrintHeader title={Z('메뉴 만드는 법', '菜品做法')} storeName={storeName} />

        {recipeGroups.length === 0 && (
          <Empty text={Z('등록된 메뉴가 없어요. 먼저 레시피 메뉴에서 메뉴를 등록해 주세요.', '还没有菜品。请先在「配方」里登记菜品。')} />
        )}

        {recipeGroups.map((group, gi) => (
          <div key={gi} style={{ marginBottom: 32 }}>
            <h2 style={{
              fontSize: 24, fontWeight: 800, color: '#18181B',
              borderBottom: '2px solid #18181B', paddingBottom: 6, marginBottom: 14,
            }}>
              {zh && group.name_zh && !hasHangul(group.name_zh) ? group.name_zh : group.name}
            </h2>

            {group.items.map((item, ii) => {
              const key = `${gi}-${ii}`;
              const isEditing = editingKey === key;

              if (isEditing) {
                return (
                  <EditingBlock
                    key={key}
                    item={editItem}
                    ingredientOptions={ingredientOptions}
                    zhMap={zhMap}
                    onField={updateField}
                    onIng={updateIng}
                    onAdd={addIng}
                    onRemove={removeIng}
                    onMove={moveIng}
                    onDragStart={onDragStart}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onSave={() => saveEdit(gi, ii)}
                    onCancel={cancelEdit}
                  />
                );
              }
              return (
                <ReadOnlyBlock
                  key={key}
                  item={item}
                  zhMap={zhMap}
                  onEdit={() => startEdit(gi, ii, item)}
                  onToggleHidden={() => toggleHidden(gi, ii)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 읽기 모드 카드 ─────────────────────────────────────────
function ReadOnlyBlock({ item, zhMap, onEdit, onToggleHidden }) {
  const { zh, Z } = useZ();
  const nameZh = zh && item.name_zh && !hasHangul(item.name_zh) ? item.name_zh : '';
  const ingName = (n) => (zh ? (zhOf(zhMap, n) || n) : n);
  const noteText = zh && item.note_zh ? item.note_zh : item.note;
  const totalWeight = (item.ingredients || []).reduce((s, ing) => s + (Number(ing.qty) || 0), 0);
  const isHidden = item.hidden === true;
  return (
    <div className={`recipe-block ${isHidden ? 'hide-on-print' : ''}`} style={{
      marginBottom: 22, paddingBottom: 18,
      borderBottom: '1px dashed #A1A1AA',
      opacity: isHidden ? 0.45 : 1,
      background: isHidden ? '#FAFAFA' : 'transparent',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#18181B' }}>
            {nameZh || item.name}
            {nameZh && nameZh !== item.name && (
              <span style={{ fontSize: 14, fontWeight: 600, color: '#A1A1AA', marginLeft: 8 }}>{item.name}</span>
            )}
            {item.serving_g > 0 && (
              <span style={{ fontSize: 16, fontWeight: 600, color: '#52525B', marginLeft: 10 }}>
                {Z(`(한 그릇 ${item.serving_g}그램)`, `（每份 ${item.serving_g}克）`)}
              </span>
            )}
            {isHidden && (
              <span style={{
                marginLeft: 10, fontSize: 13, fontWeight: 800,
                padding: '3px 10px', borderRadius: 6,
                background: '#18181B', color: '#fff',
              }}>
                {Z('인쇄 안 함', '不打印')}
              </span>
            )}
          </div>
          {totalWeight > 0 && (
            <div style={{ fontSize: 16, color: '#52525B', marginTop: 4 }}>
              {Z('재료 총 무게: ', '食材总重量：')}<strong style={{ color: '#18181B' }}>{totalWeight.toLocaleString('ko-KR')}{Z('그램', '克')}</strong>
              {item.serving_g > 0 && (
                <span style={{ marginLeft: 8 }}>
                  {Z(`→ 약 ${Math.floor(totalWeight / item.serving_g)}그릇 분량`, `→ 约 ${Math.floor(totalWeight / item.serving_g)} 份`)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={onToggleHidden}
            title={isHidden ? Z('다시 인쇄 대상에 포함', '重新打印这道菜') : Z('인쇄에서 이 메뉴 제외', '打印时不包括这道菜')}
            style={{
              padding: '8px 14px', fontSize: 15, fontWeight: 700,
              background: isHidden ? '#18181B' : '#fff',
              color: isHidden ? '#fff' : '#52525B',
              border: '2px solid ' + (isHidden ? '#18181B' : '#A1A1AA'),
              borderRadius: 6, cursor: 'pointer',
            }}>
            {isHidden ? Z('다시 표시', '重新显示') : Z('인쇄 안 함', '不打印')}
          </button>
          <button onClick={onEdit}
            style={{
              padding: '8px 16px', fontSize: 16, fontWeight: 700,
              background: '#fff', color: '#18181B',
              border: '2px solid #18181B', borderRadius: 6, cursor: 'pointer',
            }}>
            ✏️ {Z('수정', '修改')}
          </button>
        </div>
      </div>

      {item.ingredients && item.ingredients.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 18, marginTop: 6 }}>
          <thead>
            <tr style={{ background: '#FAFAFA' }}>
              <th style={th()}>{Z('재료', '食材')}</th>
              <th style={{ ...th(), textAlign: 'right', width: '15%' }}>{Z('용량', '用量')}</th>
              <th style={{ ...th(), width: '40%' }}>{Z('손질법', '处理方法')}</th>
            </tr>
          </thead>
          <tbody>
            {item.ingredients.map((ing, ix) => (
              <tr key={ix}>
                <td style={{ ...td(), fontWeight: 600 }}>{ingName(ing.name)}</td>
                <td style={{ ...td(), textAlign: 'right' }}>
                  {ing.qty}{ing.unit || Z('그램', '克')}
                </td>
                <td style={{ ...td(), color: '#52525B' }}>
                  {ing.prep || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ color: '#71717A', fontSize: 16 }}>{Z('재료가 등록되지 않았어요.', '还没有登记食材。')}</div>
      )}

      {noteText && (
        <div style={{
          marginTop: 10, padding: '10px 14px', background: '#FAFAFA',
          borderRadius: 6, fontSize: 17, color: '#52525B', lineHeight: 1.55,
        }}>
          <strong>{Z('만드는 법:', '做法：')}</strong> {noteText}
        </div>
      )}
    </div>
  );
}

// ── 편집 모드 카드 ─────────────────────────────────────────
function EditingBlock({
  item, ingredientOptions, zhMap, onField, onIng, onAdd, onRemove, onMove,
  onDragStart, onDragOver, onDrop, onSave, onCancel,
}) {
  const { zh, Z } = useZ();
  if (!item) return null;
  // V147: 중국어 화면에서는 메뉴 이름·만드는 법을 중국어 칸(name_zh·note_zh)에 적는다 (한국어 원래 이름은 그대로 — 단가표·레시피 연결이 이름으로 되어 있다)
  const totalWeight = (item.ingredients || []).reduce((s, ing) => s + (Number(ing.qty) || 0), 0);
  const servingsCount = item.serving_g > 0 ? Math.floor(totalWeight / item.serving_g) : 0;
  return (
    <div className="recipe-block no-print" style={{
      marginBottom: 22, padding: 18,
      border: '3px solid #18181B', borderRadius: 10, background: '#FAFAFA',
    }}>
      {/* 메뉴명 + 한 그릇 g */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 2, minWidth: 200 }}>
          <Label>{Z('메뉴 이름', '菜品名称（中文）')}</Label>
          <input value={(zh ? item.name_zh : item.name) || ''}
            onChange={e => onField(zh ? 'name_zh' : 'name', e.target.value)}
            style={bigInput} placeholder={zh ? (item.name || '例：炸猪排') : '예) 돈가스'} />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <Label>{Z('한 그릇 무게 (그램)', '每份重量（克）')}</Label>
          <input type="number" value={item.serving_g || ''}
            onChange={e => onField('serving_g', Number(e.target.value) || 0)}
            style={{ ...bigInput, textAlign: 'right' }} placeholder={Z('예) 200', '例：200')} />
        </div>
      </div>

      {/* 재료 표 */}
      <Label>{Z('재료', '食材')}</Label>
      <div style={{ fontSize: 14, color: '#71717A', marginBottom: 8 }}>
        {Z('손잡이(≡)를 잡고 끌어 순서를 바꾸거나, 오른쪽 ▲▼ 버튼을 눌러 옮길 수 있어요. 재료명 칸에 글자를 입력하면 단가표에 있는 재료가 자동으로 떠요.',
           '按住 ≡ 拖动，或点右边的 ▲▼ 调整顺序。在食材名称里输入文字，会自动显示单价表里的食材。')}
      </div>

      {/* 단가표 자동완성용 */}
      <datalist id="manual-ing-names">
        {(ingredientOptions || []).map(n => <option key={n} value={n} label={zh ? (zhOf(zhMap, n) || undefined) : undefined} />)}
      </datalist>

      {/* 총 무게 실시간 표시 */}
      <div style={{
        background: '#fff', border: '2px solid #18181B', borderRadius: 8,
        padding: '10px 14px', marginBottom: 10,
        fontSize: 17, fontWeight: 700, color: '#18181B',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <span>{Z('재료 총 무게:', '食材总重量：')}</span>
        <span style={{ fontSize: 22 }}>{totalWeight.toLocaleString('ko-KR')}{Z('그램', '克')}</span>
        {servingsCount > 0 && (
          <span style={{ fontSize: 15, fontWeight: 600, color: '#52525B' }}>
            {Z(`→ 약 ${servingsCount}그릇 분량`, `→ 约 ${servingsCount} 份`)}
          </span>
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
        <thead>
          <tr style={{ background: '#fff' }}>
            <th style={{ ...th(), width: 44, textAlign: 'center' }}>{Z('순서', '顺序')}</th>
            <th style={th()}>{Z('재료', '食材')}</th>
            <th style={{ ...th(), width: '15%' }}>{Z('용량(g)', '用量(g)')}</th>
            <th style={{ ...th(), width: '30%' }}>{Z('손질법', '处理方法')}</th>
            <th style={{ ...th(), width: 100, textAlign: 'center' }}>{Z('이동', '移动')}</th>
            <th style={{ ...th(), width: 60, textAlign: 'center' }}>{Z('삭제', '删除')}</th>
          </tr>
        </thead>
        <tbody>
          {(item.ingredients || []).map((ing, idx) => (
            <tr key={idx}
              draggable
              onDragStart={() => onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(idx)}
              style={{ background: '#fff' }}
            >
              <td style={{ ...td(), textAlign: 'center', cursor: 'grab',
                           fontSize: 22, color: '#71717A', userSelect: 'none' }}
                  title={Z('끌어서 순서 변경', '拖动调整顺序')}>
                ≡
              </td>
              <td style={tdEdit()}>
                <input value={ing.name || ''}
                  onChange={e => onIng(idx, 'name', e.target.value)}
                  list="manual-ing-names"
                  autoComplete="off"
                  style={bigInput} placeholder={Z('재료 이름', '食材名称')} />
              </td>
              <td style={tdEdit()}>
                <input type="number" value={ing.qty || ''}
                  onChange={e => onIng(idx, 'qty', e.target.value)}
                  style={{ ...bigInput, textAlign: 'right' }} placeholder="200" />
              </td>
              <td style={tdEdit()}>
                <input value={ing.prep || ''}
                  onChange={e => onIng(idx, 'prep', e.target.value)}
                  style={bigInput} placeholder={Z('예) 가로 1cm 썰기', '例：切 1cm 宽')} />
              </td>
              <td style={{ ...td(), textAlign: 'center' }}>
                <button onClick={() => onMove(idx, idx - 1)}
                  disabled={idx === 0}
                  style={{ ...iconBtn, opacity: idx === 0 ? 0.3 : 1 }} title={Z('위로', '上移')}>▲</button>
                <button onClick={() => onMove(idx, idx + 1)}
                  disabled={idx === (item.ingredients.length - 1)}
                  style={{ ...iconBtn, opacity: idx === (item.ingredients.length - 1) ? 0.3 : 1 }} title={Z('아래로', '下移')}>▼</button>
              </td>
              <td style={{ ...td(), textAlign: 'center' }}>
                <button onClick={() => onRemove(idx)}
                  style={{ ...iconBtn, color: '#92400E' }} title={Z('이 줄 삭제', '删除这一行')}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button onClick={onAdd}
        style={{
          marginTop: 10, padding: '12px 20px', fontSize: 17, fontWeight: 700,
          background: '#fff', color: '#18181B',
          border: '2px dashed #18181B', borderRadius: 8, cursor: 'pointer',
          width: '100%',
        }}>
        {Z('+ 재료 한 줄 추가', '+ 添加一行食材')}
      </button>

      {/* 만드는 법 */}
      <div style={{ marginTop: 18 }}>
        <Label>{Z('만드는 법', '做法（中文）')}</Label>
        <textarea value={(zh ? item.note_zh : item.note) || ''}
          onChange={e => onField(zh ? 'note_zh' : 'note', e.target.value)}
          rows={4}
          style={{ ...bigInput, fontFamily: 'inherit', resize: 'vertical' }}
          placeholder={zh ? (item.note || '例：裹上面包糠，170度油炸 5 分钟') : '예) 빵가루를 입혀 170도 기름에 5분 튀기기'} />
      </div>

      {/* 저장 / 취소 */}
      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button onClick={onCancel}
          style={{
            flex: 1, padding: '14px 20px', fontSize: 18, fontWeight: 700,
            background: '#fff', color: '#18181B',
            border: '2px solid #A1A1AA', borderRadius: 8, cursor: 'pointer',
          }}>
          {Z('취소', '取消')}
        </button>
        <button onClick={onSave}
          style={{
            flex: 2, padding: '14px 20px', fontSize: 18, fontWeight: 800,
            background: '#18181B', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer',
          }}>
          {Z('저장하기', '保存')}
        </button>
      </div>
    </div>
  );
}

const iconBtn = {
  padding: '6px 10px', margin: '0 2px', fontSize: 18, fontWeight: 700,
  background: '#fff', color: '#18181B',
  border: '2px solid #A1A1AA', borderRadius: 6, cursor: 'pointer',
  minWidth: 38,
};

function Label({ children }) {
  return (
    <div style={{ fontSize: 15, fontWeight: 700, color: '#52525B', marginBottom: 4 }}>
      {children}
    </div>
  );
}

function tdEdit() {
  return { border: '1.5px solid #A1A1AA', padding: '6px 8px', verticalAlign: 'middle' };
}

// ── 2. 단가표 인쇄 화면 ─────────────────────────────────────
function PricesView({ prices, categories, onBack, storeName }) {
  const { zh, Z, money } = useZ();
  const vendorZh = useMemo(() => {
    const m = {};
    for (const p of prices || []) { const v = (p.vendor || '').trim(); const z = (p.vendor_zh || '').trim(); if (v && z && !hasHangul(z) && !m[v]) m[v] = z; }
    return m;
  }, [prices]);
  const vendorGroups = useMemo(() => {
    const semiNames = new Set(
      (categories || []).flatMap(c => (c.items || []).filter(i => i.type === 'semi').map(i => i.name))
    );
    const menuNames = new Set(
      (categories || []).flatMap(c => (c.items || []).filter(i => i.type === 'menu').map(i => i.name))
    );
    const filtered = (prices || []).filter(p => !semiNames.has(p.name) && !menuNames.has(p.name) && p.category !== '반제품');
    const map = {};
    filtered.forEach(p => {
      const v = (p.vendor || '').trim() || '거래처 미지정';
      if (!map[v]) map[v] = [];
      map[v].push(p);
    });
    const ORDER = { '신선식품': 1, '공산품': 2, '소모품': 3, '미분류': 4 };
    return Object.keys(map).sort((a, b) =>
      a === '거래처 미지정' ? 1 : b === '거래처 미지정' ? -1 : a.localeCompare(b, 'ko')
    ).map(vendor => ({
      vendor,
      items: map[vendor].sort((a, b) => {
        const ca = ORDER[a.category || '미분류'] || 9;
        const cb = ORDER[b.category || '미분류'] || 9;
        if (ca !== cb) return ca - cb;
        return (a.name || '').localeCompare(b.name || '', 'ko');
      }),
    }));
  }, [prices, categories]);

  return (
    <div>
      <TopBar title={Z('재료 가격표', '食材价格表')} onBack={onBack} />
      <div className="manual-page" style={{
        maxWidth: 880, margin: '0 auto', padding: '24px 20px',
        fontSize: 18, color: '#18181B', lineHeight: 1.6,
      }}>
        <PrintHeader title={Z('재료 가격표', '食材价格表')} storeName={storeName} />

        {vendorGroups.length === 0 && (
          <Empty text={Z('등록된 재료가 없어요. 먼저 단가표 메뉴에서 재료를 등록해 주세요.', '还没有食材。请先在「单价表」里登记食材。')} />
        )}

        {vendorGroups.map((g, gi) => (
          <div key={gi} className="vendor-block" style={{ marginBottom: 26 }}>
            <h2 style={{
              fontSize: 24, fontWeight: 800, color: '#18181B',
              borderBottom: '2px solid #18181B', paddingBottom: 6, marginBottom: 12,
            }}>
              {g.vendor === '거래처 미지정' ? Z('거래처 미지정', '未指定供应商') : (zh && vendorZh[g.vendor] ? vendorZh[g.vendor] : g.vendor)}
            </h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
              <thead>
                <tr style={{ background: '#FAFAFA' }}>
                  <th style={th()}>{Z('분류', '分类')}</th>
                  <th style={th()}>{Z('재료 이름', '食材名称')}</th>
                  <th style={th()}>{Z('규격', '规格')}</th>
                  <th style={{ ...th(), textAlign: 'right' }}>{Z('한 봉 가격', '单价')}</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((p, i) => (
                  <tr key={i}>
                    <td style={{ ...td(), color: '#52525B', whiteSpace: 'nowrap' }}>{zh ? (CAT_ZH[p.category || '미분류'] || p.category) : (p.category || '미분류')}</td>
                    <td style={{ ...td(), fontWeight: 700 }}>{zh && p.name_zh && !hasHangul(p.name_zh) ? p.name_zh : p.name}</td>
                    <td style={{ ...td(), color: '#52525B' }}>{(zh && p.spec ? (specToZh(p.spec) || p.spec) : p.spec) || '—'}</td>
                    <td style={{ ...td(), textAlign: 'right', fontWeight: 700 }}>
                      {p.price > 0 ? money(p.price) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 3. 발주서 양식 ──────────────────────────────────────────
function OrderFormView({ onBack, storeName }) {
  const { zh, Z } = useZ();
  const blankRows = Array.from({ length: 20 });

  return (
    <div>
      <TopBar title={Z('발주서 양식', '订货单样式')} onBack={onBack} />
      <div className="manual-page" style={{
        maxWidth: 800, margin: '0 auto', padding: '24px 20px',
        fontSize: 18, color: '#18181B', lineHeight: 1.6,
      }}>
        <PrintHeader title={Z('발주서', '订货单')} storeName={storeName} />

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 18, marginBottom: 20 }}>
          <tbody>
            <tr>
              <td style={{ ...td(), background: '#FAFAFA', fontWeight: 700, width: '20%' }}>{Z('매장', '门店')}</td>
              <td style={td()}>{storeName || ''}</td>
              <td style={{ ...td(), background: '#FAFAFA', fontWeight: 700, width: '20%' }}>{Z('주문 날짜', '订货日期')}</td>
              <td style={td()}>{zh ? todayZh() : todayKo()}</td>
            </tr>
            <tr>
              <td style={{ ...td(), background: '#FAFAFA', fontWeight: 700 }}>{Z('거래처', '供应商')}</td>
              <td style={td()} colSpan={3}></td>
            </tr>
          </tbody>
        </table>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 18 }}>
          <thead>
            <tr style={{ background: '#FAFAFA' }}>
              <th style={{ ...th(), width: '8%' }}>{Z('번호', '序号')}</th>
              <th style={th()}>{Z('품목', '品名')}</th>
              <th style={{ ...th(), width: '15%', textAlign: 'right' }}>{Z('수량', '数量')}</th>
              <th style={{ ...th(), width: '20%', textAlign: 'right' }}>{Z('가격', '价格')}</th>
            </tr>
          </thead>
          <tbody>
            {blankRows.map((_, i) => (
              <tr key={i} style={{ height: 36 }}>
                <td style={{ ...td(), textAlign: 'center' }}>{i + 1}</td>
                <td style={td()}></td>
                <td style={td()}></td>
                <td style={td()}></td>
              </tr>
            ))}
            <tr>
              <td style={{ ...td(), textAlign: 'center', background: '#FAFAFA', fontWeight: 800 }} colSpan={3}>{Z('합계', '合计')}</td>
              <td style={td()}></td>
            </tr>
          </tbody>
        </table>

        <div style={{ marginTop: 24, fontSize: 16, color: '#52525B', lineHeight: 1.6 }}>
          {Z('이 양식을 인쇄해서 직접 적어 거래처에 보내실 수 있어요.', '打印出来手写后，可以发给供应商。')}
        </div>
      </div>
    </div>
  );
}

// ── 공통 셀 ────────────────────────────────────────────────
function th() {
  return {
    border: '1.5px solid #18181B', padding: '10px 12px',
    textAlign: 'left', fontWeight: 800, fontSize: 18, color: '#18181B',
  };
}
function td() {
  return { border: '1.5px solid #A1A1AA', padding: '10px 12px', fontSize: 18, verticalAlign: 'top' };
}

function Empty({ text }) {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 18, color: '#71717A', lineHeight: 1.6 }}>
      {text}
    </div>
  );
}

// ── 메인 ──────────────────────────────────────────────────────
export default function ManualPage({ categories = [], prices = [], user, ownerUid, onCategoriesChange }) {   // V147 ownerUid: 매니저도 매장 매뉴얼
  const [view, setView] = useState(null);
  const storeName = user?.displayName || '';

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <style>{PRINT_STYLES}</style>
      {view === null && <MenuChoice onSelect={setView} storeName={storeName} />}
      {view === 'recipes' && (
        <RecipesView
          categories={categories}
          prices={prices}
          onCategoriesChange={onCategoriesChange}
          onBack={() => setView(null)}
          storeName={storeName}
        />
      )}
      {view === 'prices' && (
        <PricesView prices={prices} categories={categories} onBack={() => setView(null)} storeName={storeName} />
      )}
      {view === 'order' && (
        <OrderFormView onBack={() => setView(null)} storeName={storeName} />
      )}
      {view === 'sop' && (
        <OperationsManual user={user} ownerUid={ownerUid} onBack={() => setView(null)} storeName={storeName} />
      )}
    </div>
  );
}
